/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { FC, ReactNode, useCallback, useEffect, useId, useRef } from 'react';
import { useLingui } from '@lingui/react/macro';

import Close from '@rover/icons/svg/close.svg';
import { Box, Button, Flex, Heading, Text } from '@rover/kibble/core';
import { CLOSE_TEXT } from '@rover/kibble/patterns/ScrollableModal/content.constants';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';

import { ERROR_TYPES, useOverlayContext } from './BirthdayDatePickerContext';

const FOCUS_SHEET_DELAY_MS = 10;
const SHEET_FOCUSABLE_SELECTOR =
  'select:not([disabled]), button:not([disabled]), [tabindex="0"]:not(.DayPicker-wrapper)';

export const Overlay: FC<{
  classNames: Record<string, string>;
  selectedDay?: Date;
  input?: HTMLInputElement;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  children: ReactNode;
}> = ({ children, classNames, selectedDay, input, onBlur, ...props }) => {
  const { t, i18n } = useLingui();
  const isMobile = isMobileBrowser();
  const { error, setError, handleClose } = useOverlayContext();
  const headingId = useId();
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const contactSitterEl = document.body;
    if (contactSitterEl && isMobile) {
      contactSitterEl.style.overflowY = 'hidden';
      contactSitterEl.style.touchAction = 'none';
    }

    return () => {
      contactSitterEl.style.overflowY = 'scroll';
      contactSitterEl.style.touchAction = 'auto';
    };
  });

  useEffect(() => {
    if (!isMobile) return undefined;
    const timeout = setTimeout(() => {
      sheetRef.current?.querySelector('select')?.focus();
    }, FOCUS_SHEET_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [isMobile]);

  const handleSheetKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }
      if (e.key !== 'Tab' || !sheetRef.current) return;
      const focusable = Array.from(
        sheetRef.current.querySelectorAll<HTMLElement>(SHEET_FOCUSABLE_SELECTOR)
      );
      if (!focusable.length) return;
      e.preventDefault();
      // Only one day is a tab stop, but focus can sit on any day after arrow keys or a tap.
      const active = document.activeElement as HTMLElement;
      const isDay = (el: HTMLElement): boolean => el.classList.contains('DayPicker-Day');
      const index = focusable.findIndex((el) => el === active || (isDay(el) && isDay(active)));
      const step = e.shiftKey ? -1 : 1;
      let next = e.shiftKey ? focusable.length - 1 : 0;
      if (index !== -1) next = (index + step + focusable.length) % focusable.length;
      focusable[next].focus();
    },
    [handleClose]
  );

  const handleClick = useCallback(() => {
    if (!selectedDay) {
      setError(ERROR_TYPES.EMPTY);
    } else {
      setError(ERROR_TYPES.NO_ERROR);
      handleClose(true);
    }
  }, [selectedDay, setError, handleClose]);

  const handleCancel = useCallback(() => handleClose(), [handleClose]);

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      onBlur?.(e);
      const next = e.relatedTarget as Node | null;
      if (!isMobile && next && next !== input && !e.currentTarget.contains(next)) {
        handleClose();
      }
    },
    [onBlur, isMobile, input, handleClose]
  );

  const handleOutsideClose = useCallback((evt) => {
    evt.stopPropagation();
  }, []);

  return (
    <div className={classNames.overlayWrapper} {...props} tabIndex={-1} onBlur={handleBlur}>
      <div className={classNames.overlay} onClick={handleCancel}>
        <div
          ref={sheetRef}
          className="DayPickerInput-Overlay-Container"
          onClick={handleOutsideClose}
          {...(isMobile && {
            role: 'dialog',
            'aria-modal': true,
            'aria-labelledby': headingId,
            onKeyDown: handleSheetKeyDown,
          })}
        >
          {isMobile && (
            <Flex mt="8x" justifyContent="center" alignItems="center" sx={{ position: 'relative' }}>
              <Heading id={headingId} m="0x">
                {t`Birthday`}
              </Heading>
              <Box sx={{ position: 'absolute', right: '4x' }}>
                <Button
                  icon={Close}
                  variant="flat"
                  size="small"
                  circular
                  aria-label={i18n._(CLOSE_TEXT)}
                  data-qa-id="button-close-dates"
                  onClick={handleCancel}
                />
              </Box>
            </Flex>
          )}
          {children}
          {isMobile && (
            <Box padding="4x">
              <Box role="alert">
                {error === ERROR_TYPES.EMPTY && (
                  <Box mb="6x">
                    <Text textColor="error" size="100" data-qa-id="select-date-error">
                      {t`Select date of birth`}
                    </Text>
                  </Box>
                )}
              </Box>
              <Button
                data-qa-id="button-save-dates"
                variant="primary"
                size="small"
                fullWidth
                sx={{
                  height: '8x',
                  p: '0x',
                }}
                onClick={handleClick}
              >
                {t`Save dates`}
              </Button>
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};
